import {
  FilterDocumentDateFilterTypes,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  addDays,
  addMonths,
  addQuarters,
  addYears,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';

import { weekdaysNumberMap } from './constants';

export type DeadlineDateRange = {
  start?: Date;
  end: Date;
  strictStart?: Date;
};

export const calculateDeadlinePresetDates = (
  preset: FilterDocumentDateFilterTypes,
  { today, weekStartsOn }: { today: Timestamp; weekStartsOn: WeekDays },
): DeadlineDateRange | null => {
  const weekOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };

  switch (preset) {
    case FilterDocumentDateFilterTypes.Overdue: {
      return {
        end: startOfDay(today),
      };
    }
    case FilterDocumentDateFilterTypes.Today: {
      return {
        end: endOfDay(today),
        strictStart: startOfDay(today),
      };
    }
    case FilterDocumentDateFilterTypes.ThisWeek: {
      return {
        end: endOfWeek(today, weekOptions),
        strictStart: startOfWeek(today, weekOptions),
      };
    }
    case FilterDocumentDateFilterTypes.NextWeek: {
      const nextWeekStart = addDays(today, 7);
      return {
        start: startOfWeek(nextWeekStart, weekOptions),
        end: endOfWeek(nextWeekStart, weekOptions),
        strictStart: startOfWeek(nextWeekStart, weekOptions),
      };
    }
    case FilterDocumentDateFilterTypes.Next14Days: {
      return {
        end: endOfDay(addDays(today, 14)),
        strictStart: startOfDay(today),
      };
    }
    case FilterDocumentDateFilterTypes.ThisMonth: {
      return {
        end: endOfMonth(today),
        strictStart: startOfMonth(today),
      };
    }
    case FilterDocumentDateFilterTypes.NextMonth: {
      const nextMonthStart = addMonths(today, 1);
      return {
        start: startOfMonth(nextMonthStart),
        end: endOfMonth(nextMonthStart),
        strictStart: startOfMonth(nextMonthStart),
      };
    }
    case FilterDocumentDateFilterTypes.ThisQuarter: {
      return {
        end: endOfQuarter(today),
        strictStart: startOfQuarter(today),
      };
    }
    case FilterDocumentDateFilterTypes.NextQuarter: {
      const nextQuarterStart = addQuarters(today, 1);
      return {
        start: startOfQuarter(nextQuarterStart),
        end: endOfQuarter(nextQuarterStart),
        strictStart: startOfQuarter(nextQuarterStart),
      };
    }
    case FilterDocumentDateFilterTypes.ThisYear: {
      return {
        end: endOfYear(today),
        strictStart: startOfYear(today),
      };
    }
    case FilterDocumentDateFilterTypes.NextYear: {
      const nextYearStart = addYears(today, 1);
      return {
        start: startOfYear(nextYearStart),
        end: endOfYear(nextYearStart),
        strictStart: startOfYear(nextYearStart),
      };
    }
    case FilterDocumentDateFilterTypes.HasDeadline:
    case FilterDocumentDateFilterTypes.NoDeadline:
    case FilterDocumentDateFilterTypes.Custom:
    default: {
      return null;
    }
  }
};
